import { ReactNode, useState, useLayoutEffect, Children } from 'react';

import styles from './TwoColumnGrid.module.scss';

// Define interfaces for props for better type checking
interface ChildProps {
  children: ReactNode;
  mobileOrder?: number;
}

interface SlimProps {
  children: ReactNode;
  isSticky?: boolean;
  top?: number;
  mobileOrder?: number;
}

interface TwoColumnGridProps {
  children: ReactNode;
  className?: string;
}

// Compound components
const Wide = ({ children, mobileOrder }: ChildProps) => {
  if (Children.count(children) === 0) {
    return null;
  }

  const style: React.CSSProperties = {
    ...(mobileOrder !== undefined && { '--mobile-order': mobileOrder }),
  } as React.CSSProperties;

  return (
    <div className={styles.wide} style={style}>
      {children}
    </div>
  );
};
Wide.displayName = 'TwoColumnGrid.Wide';

const Slim = ({ children, isSticky, top, mobileOrder }: SlimProps) => {
  const [offsetTop, setOffsetTop] = useState(0);

  useLayoutEffect(() => {
    if (isSticky) {
      if (top !== undefined) {
        setOffsetTop(top);
      } else {
        const header =
          document.querySelector<HTMLElement>('[data-id="header"]');
        const banner = document.querySelector<HTMLElement>(
          '[data-id="sticky-banner-container"]',
        );

        const headerHeight = header?.offsetHeight || 0;
        const bannerHeight = banner?.offsetHeight || 0;

        setOffsetTop(headerHeight + bannerHeight + 16);
      }
    }
  }, [isSticky, top]);

  if (Children.count(children) === 0) {
    return null;
  }

  const slimClasses = [styles.slim, isSticky ? styles.sticky : '']
    .filter(Boolean)
    .join(' ');

  const style: React.CSSProperties = {
    ...(isSticky && { top: `${offsetTop}px` }),
    ...(mobileOrder !== undefined && { '--mobile-order': mobileOrder }),
  } as React.CSSProperties;

  return (
    <div className={slimClasses} style={style}>
      {children}
    </div>
  );
};
Slim.displayName = 'TwoColumnGrid.Slim';

// Main layout component
const TwoColumnGrid = ({ children, className }: TwoColumnGridProps) => {
  const containerClasses = [styles.twoColumnGrid, className]
    .filter(Boolean)
    .join(' ');

  return <div className={containerClasses}>{children}</div>;
};

// Attach compound components to the main component
TwoColumnGrid.Wide = Wide;
TwoColumnGrid.Slim = Slim;

export default TwoColumnGrid;
